/**
 * LeavePolicySettingsSection — tenant-wide annual leave allocation admin
 * form (Prompt 11E.6). Injected into Settings via render-prop at the route
 * composition layer (src/routes/index.tsx) — Settings must not import from
 * Attendance directly (ESLint feature-boundary rule).
 */

import React, { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { Card, CardHeader, CardTitle, Button, Input } from '@/components/common';
import { useLeavesConfig, useUpdateLeavesConfig } from '@features/attendance/api/leaves';
import type { ApiError } from '@core/api/client';

export const LeavePolicySettingsSection: React.FC = () => {
  const { data: config, isLoading } = useLeavesConfig();
  const updateConfig = useUpdateLeavesConfig();
  const [allocation, setAllocation] = useState<number | ''>('');

  useEffect(() => {
    if (config) setAllocation(config.annual_leave_allocation);
  }, [config]);

  const handleSave = async () => {
    if (allocation === '' || Number.isNaN(Number(allocation))) {
      toast.error('Enter a valid number of days.');
      return;
    }
    try {
      await updateConfig.mutateAsync(Number(allocation));
      toast.success('Leave policy updated.');
    } catch (err) {
      toast.error((err as ApiError).message || 'Could not update the leave policy.');
    }
  };

  return (
    <Card padding="lg" data-testid="candeza-leave-policy-settings">
      <CardHeader>
        <CardTitle>Leave Policy</CardTitle>
      </CardHeader>
      <p style={{ fontSize: 14, color: '#64748b', margin: '4px 0 20px' }}>
        Annual paid-leave allocation — same for every staff member. Per-staff overrides are not
        supported yet.
      </p>
      <Input
        label="Annual leave allocation (days)"
        type="number"
        min={config?.annual_leave_allocation_min}
        max={config?.annual_leave_allocation_max}
        value={allocation}
        onChange={(e) => setAllocation(e.target.value === '' ? '' : Number(e.target.value))}
        disabled={isLoading}
        hint={
          config
            ? `Range: ${config.annual_leave_allocation_min}–${config.annual_leave_allocation_max} days`
            : undefined
        }
        data-testid="candeza-leave-policy-allocation"
      />
      <div style={{ marginTop: 20, display: 'flex', justifyContent: 'flex-end' }}>
        <Button
          onClick={handleSave}
          disabled={updateConfig.isPending || isLoading}
          data-testid="candeza-leave-policy-save"
        >
          {updateConfig.isPending ? 'Saving…' : 'Save Changes'}
        </Button>
      </div>
    </Card>
  );
};

export default LeavePolicySettingsSection;
